import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot } from "@angular/router";
import { Permission } from "@nucleus/user-ui";
import { PermissionFilter } from "./permission-filter";
import { SearchService } from "@nucleus/ui-base";
import { ApplicationConfigService } from "../application-config.service";
import { ServiceHandler } from "@nucleus/ui-base";
import { PageResult } from "@nucleus/ui-base";
import { Page, QuerySortDirection } from "@nucleus/ui-base";
import { PermissionControllerService } from "@nucleus/user-ui";
import { RequestHandler } from "../request-handler";
import { RoleConstants } from "../../../common/roles.constrants";

@Injectable({
    providedIn: 'root'
  })
  export class PermissionsService  implements SearchService<Permission, PermissionFilter>{

    constructor(
      private configService : ApplicationConfigService,
      private api: PermissionControllerService,
      private requestHandler: RequestHandler) {}

    search(handler: ServiceHandler<Permission[]>): void {
      this.requestHandler.handleRequest(this.api.findAllPermissions() as unknown as Observable<Permission[]>, handler, false);
    }

    searchPaginated(handler: ServiceHandler<PageResult<Permission>>, pageSize: number, pageNumber: number, softField: string, sortDirection: QuerySortDirection): void {
      throw new Error("Method not implemented.");
    }

    filterPaginated(handler: ServiceHandler<PageResult<Permission>>, pageSize: number, pageNumber: number, softField: string, sortDirection: QuerySortDirection, filter: PermissionFilter): void {
      throw new Error("Method not implemented.");
    }

    filter(handler: ServiceHandler<PageResult<Permission>>, filter: PermissionFilter, page?: Page, _clientId?: string): void {
      throw new Error("Method not implemented.");
    }

    canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
      console.log(`checking for ${next.url.toString()}`);
      return this.hasAccess(next.url.toString());
    }


    /**
     * Route-authorisation check used by AuthGuard. Default-allow for routes
     * that have NO rule entry — matches the pre-refactor client logic where
     * nav-rules.find(r => r.route === path) returned undefined → true. Only
     * routes that ARE rule-gated and the user does not match get denied.
     *
     * NOT for per-action authority checks — the input is a ROUTE PATH, not
     * an authority code. Use hasAuthority() for per-action gates
     * (PermissionCheckerService).
     */
    hasAccess(path: string) : boolean {
      if (!this.configService.isRouteKnown(path)) {
        return true;
      }
      return this.configService.isRoutePermitted(path);
    }

    /**
     * Direct authority check — true iff the current user holds the given
     * authority code. Use this for per-action gates
     * (PermissionCheckerService.hasAccessToXxx); use hasAccess() only for
     * route-level navigation guarding.
     */
    hasAuthority(code: string) : boolean {
      return this.configService.getAuthorities().includes(code);
    }

    /** True iff the user holds at least one of the given authority codes.
     *  Convenience for OR-logic gates (e.g. \"granular code OR coarse fallback\"). */
    hasAnyAuthority(...codes: string[]) : boolean {
      const authorities = this.configService.getAuthorities();
      return codes.some(c => authorities.includes(c));
    }

    /**
     * NUC-179: NucleusCapabilityContract.hasCapability — the single capability
     * gate every nucleus-* lib (e.g. @nucleus/connector-admin-ui) injects via
     * CAPABILITY_CHECKER. In TaskSense's authority model, capability strings
     * (`entity:action`) ARE authority codes, with ROLE_ADMIN as the universal
     * escape hatch every gate carries. Delegates straight to hasAnyAuthority.
     */
    hasCapability(capability: string) : boolean {
      return this.hasAnyAuthority(capability, RoleConstants.ROLE_ADMIN);
    }

  }

  export const AuthGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean => {
    return inject(PermissionsService).canActivate(next, state);
  }

  /** NUC-152: route-level feature gate. Mirrors the menu *ngIf so URL-typed
   *  navigation also gets blocked when the tenant has the feature disabled. */
  export const FeatureGuard = (featureKey: string): CanActivateFn =>
    () => localStorage.getItem(`feature.${featureKey}`) === 'true';
